// A month-by-month chart: bars and lines over one shared time axis.
//
// The trends screen's one drawing primitive. It knows nothing about deals or
// money: a page hands it months, series and a value formatter, and it draws
// them, handles hover and focus, and reports nothing back. Every choice here
// follows the dataviz method rather than taste:
//
//   * ONE AXIS. Every series on a chart shares a y-scale, so a page that wants
//     to show a count beside an amount draws two charts. There is no second
//     axis to invent a correlation with.
//   * THIN MARKS, RECESSIVE CHROME. Hairline gridlines one shade off the
//     surface, 2px lines, 8px markers, a 2px surface gap between adjacent bars.
//   * COLOUR FOLLOWS THE SERIES, never its rank: the caller assigns a slot to
//     each series and the slot is what the swatch, the mark and the tooltip key
//     all use. Text wears text tokens only.
//   * A LEGEND FOR TWO OR MORE SERIES, none for one — the card's title names a
//     lone series. Identity is never colour alone: the legend mirrors the mark
//     (a rect for a bar, a stroke for a line) beside the name.
//   * HOVER BY DEFAULT. A crosshair snaps to the nearest month and one tooltip
//     lists every series at that month, values first; the same readout is
//     reachable from the keyboard, because each month is a focusable target.
//   * NEVER THE ONLY WAY TO READ THE DATA. The page under it carries a table
//     with every figure, so the chart is an enrichment and is marked up that
//     way: the figure has an accessible name, and each month target announces
//     its readout as a sentence.
//
// WHY SVG AND NOT A CHARTING LIBRARY. The geo map already set the precedent:
// nothing to configure, nothing to keep in step with a CSP, and a few hundred
// lines that draw exactly what this screen needs. What is given up — brushing,
// animation, log scales — is nothing a monthly book of business asks for.

import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import styles from "./MonthlyChart.module.css";
import { formatMonth } from "../../format/calendarDate.js";

/** One of the three categorical slots in tokens.css. */
export type ChartSlot = 1 | 2 | 3;

export interface ChartSeries {
  key: string;
  label: string;
  kind: "bar" | "line";
  /** One value per month, in the same order as `months`. */
  values: readonly number[];
  slot: ChartSlot;
  /**
   * Draw a bar series hatched rather than solid. For a figure that is NOT the
   * same kind of thing as the solid bars beside it — an estimate stacked on
   * real money — so the difference survives a glance, a greyscale print and a
   * reader who cannot tell the slot colours apart. The legend and the tooltip
   * key mirror it.
   */
  hatched?: boolean;
  /**
   * A line drawn dashed with hollow markers: the same thing as the solid line
   * in its slot, but not yet done (still to bill beside billed). Lines only.
   * Identity still never rests on the dash alone — the legend names it.
   */
  dashed?: boolean;
}

export interface MonthlyChartProps {
  /** Names the figure for assistive tech, e.g. "Deals closed by month". */
  label: string;
  months: readonly string[];
  series: readonly ChartSeries[];
  /** How a value reads in the tooltip and the readout. */
  formatValue: (value: number) => string;
  /** How a value reads on the axis. Shorter than `formatValue`; defaults to it. */
  formatTick?: (value: number) => string;
  /**
   * Per-month caveats, e.g. "2 bills could not resolve a split". Drawn as a
   * marker under the month and said in the tooltip, so a low month with a
   * caveat reads as "unknown" rather than "down".
   */
  caveats?: readonly (string | null)[];
  /** Stack the bar series on top of each other instead of side by side. */
  stacked?: boolean;
}

// The drawing frame, in user units. The frame is MEASURED rather than fixed:
// one user unit is one CSS pixel, whatever the card is. A fixed viewBox scaled
// to the container instead — the obvious way to draw a responsive SVG — makes
// every mark in it grow with the card, and this page puts one chart in a card
// twice the width of the others: at 1400px a 720-unit frame renders 10.5px
// ticks at 20px, 2px lines at 4px and a 230-unit chart 450px tall, next to
// siblings drawn at half that. Measuring keeps type, strokes and height the
// same on every card and gives a wide card more room per month, which is what
// the extra width is for.
const FALLBACK_WIDTH = 720;
/** Narrower than this and the margins eat the plot; the SVG scales down instead. */
const MIN_WIDTH = 360;
/** The frame's height: the nominal one, and how tall a wide card may grow it. */
const HEIGHT = 230;
const MAX_HEIGHT = 300;
/** A chart wider than HEIGHT / this ratio gets height back, up to MAX_HEIGHT. */
const MIN_ASPECT = 4.4;
const MARGIN = { top: 14, right: 12, bottom: 30, left: 62 } as const;

/** Bars fill this much of a month's slot; the rest is breathing room. */
const BAR_SLOT_SHARE = 0.68;
/** ...but never grow past this, so a wide card gets more air, not fatter bars. */
const MAX_BAR_WIDTH = 34;
/** The surface gap between adjacent bars and stacked segments. */
const BAR_GAP = 2;
const MARKER_RADIUS = 4;
const TICK_COUNT = 4;

interface Hover {
  index: number;
  /** Client coordinates, so the tooltip can be positioned over the page. */
  x: number;
  y: number;
}

/**
 * A "nice" upper bound for the axis: 1, 2, 2.5 or 5 times a power of ten, so
 * the gridlines land on round numbers rather than on max/4.
 */
export function niceCeiling(value: number): number {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const fraction = value / magnitude;
  const nice =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * magnitude;
}

/**
 * The y-domain: always includes zero (a bar without a baseline is a bar with
 * no meaning), and extends below it only when a value is negative — a credit
 * note in an invoiced month.
 */
export function yDomain(
  series: readonly ChartSeries[],
  stacked: boolean,
): { min: number; max: number } {
  let max = 0;
  let min = 0;
  const bars = series.filter((s) => s.kind === "bar");
  const monthCount = series[0]?.values.length ?? 0;
  for (let i = 0; i < monthCount; i += 1) {
    let positive = 0;
    let negative = 0;
    for (const s of series) {
      const v = s.values[i] ?? 0;
      if (stacked && s.kind === "bar") {
        if (v >= 0) positive += v;
        else negative += v;
      } else {
        max = Math.max(max, v);
        min = Math.min(min, v);
      }
    }
    if (stacked && bars.length > 0) {
      max = Math.max(max, positive);
      min = Math.min(min, negative);
    }
  }
  // `min === 0` rather than `-niceCeiling(0)`, which is -0 and a different number to a test.
  return { min: min === 0 ? 0 : -niceCeiling(-min), max: niceCeiling(max) };
}

/**
 * The element's own width in CSS pixels, so the drawing frame can be its own
 * size. `null` until the first measurement — and on a platform without a
 * ResizeObserver, forever — and the caller falls back to the nominal width.
 */
function useMeasuredWidth(): { ref: (node: HTMLDivElement | null) => void; width: number | null } {
  const [width, setWidth] = useState<number | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured !== undefined) setWidth(measured);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/** Which month labels to draw: every month up to a year, then a thinning. */
function labelEvery(count: number): number {
  if (count <= 13) return 1;
  return Math.ceil(count / 12);
}

export function MonthlyChart({
  label,
  months,
  series,
  formatValue,
  formatTick,
  caveats,
  stacked = false,
}: MonthlyChartProps): ReactNode {
  const titleId = useId();
  // `useId` yields colons, which are legal in an id but not inside `url(#…)`.
  const patternId = `hatch${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const tick = formatTick ?? formatValue;

  // The frame is the figure's own width, so one user unit is one CSS pixel.
  const { ref: figureRef, width: measured } = useMeasuredWidth();
  const width = Math.max(measured ?? FALLBACK_WIDTH, MIN_WIDTH);
  // A card twice the width should not be a chart half as tall to read: the
  // frame gets some of the extra width back as height, to a ceiling, so a
  // full-width card is a chart and not a strip.
  const height = Math.round(Math.min(Math.max(HEIGHT, width / MIN_ASPECT), MAX_HEIGHT));
  const plotWidth = width - MARGIN.left - MARGIN.right;
  const plotHeight = height - MARGIN.top - MARGIN.bottom;

  const domain = useMemo(() => yDomain(series, stacked), [series, stacked]);
  const span = domain.max - domain.min || 1;
  const y = useCallback(
    (value: number) => MARGIN.top + plotHeight - ((value - domain.min) / span) * plotHeight,
    [domain.min, span, plotHeight],
  );
  const zeroY = y(0);

  const slotWidth = months.length === 0 ? plotWidth : plotWidth / months.length;
  const xCenter = (index: number) => MARGIN.left + slotWidth * (index + 0.5);

  const bars = series.filter((s) => s.kind === "bar");
  const lines = series.filter((s) => s.kind === "line");
  const hatchedSlots = [...new Set(bars.filter((s) => s.hatched === true).map((s) => s.slot))];

  // Gridlines at even fractions of the domain, zero always among them.
  const ticks = useMemo(() => {
    const values: number[] = [];
    for (let i = 0; i <= TICK_COUNT; i += 1) values.push(domain.min + (span * i) / TICK_COUNT);
    if (!values.some((v) => v === 0)) values.push(0);
    return values.sort((a, b) => a - b);
  }, [domain.min, span]);

  /** The month under a pointer, from the SVG's own coordinate frame. */
  const indexAt = useCallback(
    (clientX: number): number | null => {
      const svg = svgRef.current;
      if (svg === null || months.length === 0) return null;
      const rect = svg.getBoundingClientRect();
      const userX = ((clientX - rect.left) / rect.width) * width;
      const index = Math.floor((userX - MARGIN.left) / slotWidth);
      if (index < 0 || index >= months.length) return null;
      return index;
    },
    [months.length, slotWidth, width],
  );

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const index = indexAt(event.clientX);
    if (index === null) {
      setHover(null);
      return;
    }
    setHover({ index, x: event.clientX, y: event.clientY });
  };

  const onFocusMonth = (index: number) => (event: ReactFocusEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHover({ index, x: rect.left + rect.width / 2, y: rect.top + 12 });
  };

  const readout = (index: number): string => {
    const month = months[index] ?? "";
    const parts = series.map((s) => `${s.label} ${formatValue(s.values[index] ?? 0)}`);
    const caveat = caveats?.[index] ?? null;
    return `${formatMonth(month)}: ${parts.join(", ")}${caveat === null ? "" : `. ${caveat}`}`;
  };

  const every = labelEvery(months.length);
  // The group fills its share of the slot, capped: past the cap the extra room
  // a wide card brings becomes space between months, not heavier bars.
  const groupWidth = Math.min(
    slotWidth * BAR_SLOT_SHARE,
    (stacked ? 1 : bars.length) * MAX_BAR_WIDTH + (stacked ? 0 : BAR_GAP * (bars.length - 1)),
  );
  const barWidth =
    bars.length === 0
      ? 0
      : (groupWidth - (stacked ? 0 : BAR_GAP * (bars.length - 1))) / (stacked ? 1 : bars.length);

  return (
    <div className={styles.figure} ref={figureRef}>
      <svg
        ref={svgRef}
        className={styles.svg}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setHover(null)}
      >
        <title id={titleId}>{label}</title>

        {/* One hatch per slot a hatched series uses. Declared under the slot's
            class so the pattern's marks resolve that slot's colour: a pattern
            inherits from where it is DEFINED, not from the bar that uses it. */}
        {hatchedSlots.length > 0 && (
          <defs>
            {hatchedSlots.map((slot) => (
              <pattern
                key={slot}
                id={`${patternId}-${slot}`}
                className={styles[`slot${slot}`]}
                patternUnits="userSpaceOnUse"
                width={6}
                height={6}
                patternTransform="rotate(45)"
              >
                <rect className={styles.hatchGround} width={6} height={6} />
                <line className={styles.hatchLine} x1={0} y1={0} x2={0} y2={6} />
              </pattern>
            ))}
          </defs>
        )}

        {/* Grid and axis labels: recessive, solid hairlines. */}
        {ticks.map((value) => (
          <g key={value}>
            <line
              className={value === 0 ? styles.baseline : styles.grid}
              x1={MARGIN.left}
              x2={width - MARGIN.right}
              y1={y(value)}
              y2={y(value)}
            />
            <text
              className={styles.tick}
              x={MARGIN.left - 8}
              y={y(value)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {tick(value)}
            </text>
          </g>
        ))}

        {/* Bars, grouped or stacked, with a surface gap between fills. */}
        {months.map((month, index) => {
          let positiveTop = 0;
          let negativeTop = 0;
          const groupLeft = xCenter(index) - groupWidth / 2;
          return (
            <g key={month}>
              {bars.map((s, barIndex) => {
                const value = s.values[index] ?? 0;
                if (value === 0) return null;
                let from: number;
                let to: number;
                if (stacked) {
                  if (value >= 0) {
                    from = positiveTop;
                    positiveTop += value;
                    to = positiveTop;
                  } else {
                    from = negativeTop;
                    negativeTop += value;
                    to = negativeTop;
                  }
                } else {
                  from = 0;
                  to = value;
                }
                const top = Math.min(y(from), y(to));
                const height = Math.abs(y(from) - y(to));
                // The gap sits on the segment away from the baseline, so the
                // first segment is still anchored to zero.
                const gap = stacked && from !== 0 ? BAR_GAP : 0;
                const x = stacked ? groupLeft : groupLeft + barIndex * (barWidth + BAR_GAP);
                return (
                  <rect
                    key={s.key}
                    className={`${styles.bar} ${styles[`slot${s.slot}`]} ${s.hatched === true ? styles.hatched : ""} ${hover?.index === index ? styles.lit : ""}`}
                    // Inline, because a class's `fill` outranks a presentation attribute.
                    {...(s.hatched === true
                      ? { style: { fill: `url(#${patternId}-${s.slot})` } }
                      : {})}
                    x={x}
                    y={value >= 0 ? top + gap : top}
                    width={Math.max(barWidth, 1)}
                    height={Math.max(height - gap, 0)}
                    rx={2}
                  />
                );
              })}
            </g>
          );
        })}

        {/* Lines over the bars, with a marker at every month. */}
        {lines.map((s) => {
          const points = months.map((_, index) => `${xCenter(index)},${y(s.values[index] ?? 0)}`);
          return (
            <g
              key={s.key}
              className={`${styles[`slot${s.slot}`]} ${s.dashed === true ? styles.dashed : ""}`}
            >
              {points.length > 1 && <polyline className={styles.line} points={points.join(" ")} />}
              {months.map((month, index) => (
                <circle
                  key={month}
                  className={`${styles.marker} ${hover?.index === index ? styles.lit : ""}`}
                  cx={xCenter(index)}
                  cy={y(s.values[index] ?? 0)}
                  r={MARKER_RADIUS}
                />
              ))}
            </g>
          );
        })}

        {/* Month labels and caveat markers. */}
        {months.map((month, index) => (
          <g key={month}>
            {index % every === 0 && (
              <text
                className={styles.tick}
                x={xCenter(index)}
                y={height - MARGIN.bottom + 16}
                textAnchor="middle"
              >
                {shortMonth(month)}
              </text>
            )}
            {caveats?.[index] != null && (
              <circle
                className={styles.caveat}
                cx={xCenter(index)}
                cy={height - MARGIN.bottom + 24}
                r={2.5}
              >
                <title>{caveats[index]}</title>
              </circle>
            )}
          </g>
        ))}

        {/* The crosshair, snapped to the hovered month. */}
        {hover !== null && (
          <line
            className={styles.crosshair}
            x1={xCenter(hover.index)}
            x2={xCenter(hover.index)}
            y1={MARGIN.top}
            y2={MARGIN.top + plotHeight}
          />
        )}

        {/* Keyboard targets: one per month, the whole slot, over everything. */}
        {months.map((month, index) => (
          <rect
            key={month}
            className={styles.target}
            x={MARGIN.left + slotWidth * index}
            y={MARGIN.top}
            width={slotWidth}
            height={plotHeight}
            tabIndex={0}
            role="img"
            aria-label={readout(index)}
            onFocus={onFocusMonth(index)}
            onBlur={() => setHover(null)}
          />
        ))}
      </svg>

      {series.length >= 2 && (
        <ul className={styles.legend} aria-label="Series">
          {series.map((s) => (
            <li key={s.key} className={styles.legendItem}>
              <span
                className={`${styles.swatch} ${s.kind === "line" ? styles.swatchLine : styles.swatchBar} ${s.hatched === true ? styles.swatchHatched : ""} ${styles[`slot${s.slot}`]} ${s.dashed === true ? styles.dashed : ""}`}
                aria-hidden="true"
              />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      {hover !== null && (
        <div className={styles.tooltip} style={{ left: hover.x, top: hover.y }} role="presentation">
          <div className={styles.tooltipTitle}>{formatMonth(months[hover.index] ?? "")}</div>
          {series.map((s) => (
            <div key={s.key} className={styles.tooltipLine}>
              <span
                className={`${styles.tooltipKey} ${s.hatched === true ? styles.swatchHatched : ""} ${styles[`slot${s.slot}`]} ${s.dashed === true ? styles.dashed : ""}`}
                aria-hidden="true"
              />
              <span className={styles.tooltipValue}>{formatValue(s.values[hover.index] ?? 0)}</span>
              <span className={styles.tooltipLabel}>{s.label}</span>
            </div>
          ))}
          {caveats?.[hover.index] != null && (
            <div className={styles.tooltipCaveat}>{caveats[hover.index]}</div>
          )}
        </div>
      )}
    </div>
  );
}

/** `2026-09` -> `Sep`, or `Jan 2027` at a year boundary so the axis stays legible. */
function shortMonth(isoMonth: string): string {
  const full = formatMonth(isoMonth);
  return isoMonth.endsWith("-01") ? full : full.slice(0, 3);
}
